import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { withRouter } from '@/hooks';
import * as Sentry from '@sentry/react';
import 'leaflet/dist/leaflet.css';

import { type Location, type Params, type NavigateFunction, useNavigate } from 'react-router-dom';
import { setProcessHistory, setHistoryLoading, setProcessPageCache } from '@/actions/plannings/pure';

declare const VERSION: string;

import {
  SetRecentPlanningDate,
  setSelectedProcess,
} from '@/actions/common/commonPure';
import { background, dispatch_theme } from '@/config/colors';

import { AppDispatch, RootState } from '../../index';
import { ProtectedRoute } from '@/containers/ProtectedRoute';
import auth from '@/apis/auth';
import processApi from '@/apis/processApi';
import { AppContent } from './AppContent';
import { SET_CURRENT_PAGE, SET_TOTAL_PAGES, SET_TOTAL_PLANNING_COUNT } from '@/actions/types';
import { ToastProvider } from '../Dashboard/ToastContext';

const useStyles = makeStyles((): any => ({
  root: {
    flexGrow: 1,
    alignItems: 'stretch',
    height: '94.5%',
  },
  background: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: '-1',
    backgroundColor: background.overlay,
  },
  padding: {
    flexGrow: 1,
    overflow: 'visible',
    overflowY: 'hidden',
    padding: dispatch_theme.spacing(0, 0, 3, 9.875),
    transition: 'transform 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
  },
  grid: {
    padding: 0,
    overflow: 'hidden',
  },
}));

export type AppProps = {
  children: any;
  router: {
    location: Location;
    navigate: NavigateFunction;
    params: Params;
  };
}

const App: React.FC<AppProps> = (props) => {
  const [state, setState] = useState({
    initialLoad: true,
  });
  const { loading } = useSelector((state: RootState) => state.core);
  const login = useSelector((state: RootState) => state.login);
  const { processHistory } = useSelector((state: RootState) => state.planning);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = auth.loggedIn();
  const { selected_process, recent_planning_date } = useSelector((state: RootState) => state.core);
  const { location: { pathname }, params: { id: paramProcessId } } = props.router;

  let processId = paramProcessId;
  if (!processId) {
    const parts = pathname.split('/');
    const maybeUUID = parts[parts.length - 1];
    if (/^[a-f0-9-]{36}$/.test(maybeUUID)) {
      processId = maybeUUID;
    }
  }

  useEffect(() => {
    if (!isLoggedIn) return;
    Sentry.withScope(function (scope) {
      const { user = '-' } = login;
      scope.setUser({ email: user });
    });

    setState({ initialLoad: false });
    return () => {
      dispatch(setSelectedProcess(null));
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !processId) return;
    //@ts-ignore
    const fromHistory = processHistory.find(p => p.optimization_id === processId);
    if (fromHistory) {
      dispatch(setSelectedProcess(fromHistory));
      dispatch(SetRecentPlanningDate(fromHistory.timestamp));
    } else {
      dispatch(setSelectedProcess({ optimization_id: processId } as any));
    }
  }, [isLoggedIn, processId, dispatch, processHistory]);

  useEffect(() => {
    if (!isLoggedIn) return;

    setState({ initialLoad: false });

    const setDefaultsFrom = (list: any[]) => {
      const latest = list?.[0];
      if (!latest) return;

      if (!selected_process && !processId) {
        dispatch(setSelectedProcess(latest));
        navigate(`/dashboard/${latest.optimization_id}`);
      }

      if (!recent_planning_date) {
        dispatch(SetRecentPlanningDate(latest.timestamp));
      }
    };

    const loadInitialData = async () => {
      dispatch(setHistoryLoading(true));
      try {
        const { data } = await processApi.getProcessIds(1);
        const { results, count, total_pages } = data;
        if (results?.length) {
          setDefaultsFrom(results);
          dispatch(setProcessHistory(results));
          dispatch({ type: SET_TOTAL_PAGES, data: total_pages });
          dispatch({ type: SET_CURRENT_PAGE, data: 1 });
          dispatch({ type: SET_TOTAL_PLANNING_COUNT, data: count });
          dispatch(setProcessPageCache(1, results));
        } else {
          //@ts-ignore
          dispatch(setProcessHistory([]));
          dispatch(setSelectedProcess(null));
        }
      } catch (err) {
        console.debug('Failed to fetch first page of process data:', err);
      } finally {
        dispatch(setHistoryLoading(false));
      }
    };
    loadInitialData();
    return () => {
      dispatch(setSelectedProcess(null));
    };
  }, [isLoggedIn, login.user]);

  return (
    <ProtectedRoute {...props}>
      <div className={'base'}>
        {(state.initialLoad || loading) && (
          <LinearProgress
            style={{ position: 'fixed', top: 0, left: 0, right: 0 }}
            variant="indeterminate"
            color={'secondary'}
          />
        )}
         <ToastProvider>
            <AppContent initialLoad={state.initialLoad} />
         </ToastProvider>
      </div>
    </ProtectedRoute>
  );
};

export default withRouter(App);
